import { Select } from '@/components/ui/Select'

/** Sentinel key representing "no filter" in a FilterSelect. */
export const FILTER_ALL_KEY = '__all__'

export interface FilterSelectOption {
  value: string
  label: string
}

export interface FilterSelectProps {
  /** Accessible name for the select (also the inline label / placeholder). */
  label: string
  /** Currently selected value; null means "all". */
  value: string | null
  /** Pre-translated options (excluding the "all" entry, which is added here). */
  options: FilterSelectOption[]
  onChange: (value: string | null) => void
  /** Label of the injected "all" option. */
  anyLabel: string
  /** Render the label as inline text before the select (picker style). */
  showLabel?: boolean
  /** Width class for the select wrapper (defaults to w-40). */
  className?: string
}

/**
 * A small facet filter dropdown with an injected "All" option. Shared by the
 * internal voice admin list and the customer-facing voice picker.
 */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  anyLabel,
  showLabel = false,
  className,
}: FilterSelectProps) {
  const selectOptions = [{ value: FILTER_ALL_KEY, label: anyLabel }, ...options]
  const select = (
    <div className={className ?? 'w-40'}>
      <Select
        aria-label={label}
        placeholder={showLabel ? undefined : label}
        options={selectOptions}
        selectedKey={value ?? FILTER_ALL_KEY}
        onSelectionChange={(key) => onChange(key === FILTER_ALL_KEY ? null : String(key))}
      />
    </div>
  )
  if (!showLabel) return select
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      {select}
    </div>
  )
}

export default FilterSelect
